import BackArrowButton from "../backArrowButton/backArrowButton";
import styles from "./userComments.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import { useRef, useState } from "react";
import useAutosizeTextArea from "../../hooks/useAutosizeTextarea";
import { noDataLabel } from "../../utils/constants";
import { getPrintDate, getTimeFromTimestamp } from "../../utils/utils";
import { useAppSelector } from "../../services/hooks";
import editIcon from "../../images/edit_icon.svg";
import { adminSelector } from "../../services/selectors/user.selectors";
import { IUserReport } from "../../services/types/users.types";
import {
  useGetUserCommentsQuery,
  usePostUserCommentMutation,
  usePatchUserCommentMutation,
  useGetUserAdminCommentsQuery,
  usePostUserAdminCommentMutation,   // 👈 add these two to your api
  usePatchUserAdminCommentMutation,  // 👈
} from "../../services/api/user.api";
import formStyles from "../../components/forms/forms.module.css";

export type UserCommentsProps = {
  onClose?: () => void;
  user: IUserReport["Users"];
};

function UserComments({ onClose, user }: UserCommentsProps) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const editTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const admin = useAppSelector(adminSelector);

  // Staff comments
  const { data: comments, isLoading, isError } = useGetUserCommentsQuery(user.Guid);
  const [postComment, { isLoading: isPosting }] = usePostUserCommentMutation();
  const [patchComment, { isLoading: isPatching }] = usePatchUserCommentMutation();

  // Admin comments
  const { data: adminComments, isLoading: isAdminLoading, isError: isAdminError } = useGetUserAdminCommentsQuery(user.Guid);
  const [postAdminComment, { isLoading: isAdminPosting }] = usePostUserAdminCommentMutation();
  const [patchAdminComment, { isLoading: isAdminPatching }] = usePatchUserAdminCommentMutation();

  const [activeTab, setActiveTab] = useState<'comments' | 'adminComments'>('comments');
  const [value, setValue] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");

  useAutosizeTextArea(editTextAreaRef.current, editDraft);
  useAutosizeTextArea(textAreaRef.current, value || "");

  // Derive active data/handlers based on tab
  const isAdminTab = activeTab === 'adminComments';
  const activeComments = isAdminTab ? adminComments : comments;
  const activeIsLoading = isAdminTab ? isAdminLoading : isLoading;
  const activeIsError = isAdminTab ? isAdminError : isError;
  const activeIsPosting = isAdminTab ? isAdminPosting : isPosting;
  const activeIsPatching = isAdminTab ? isAdminPatching : isPatching;

  const handleSendMessage = (val: string) => {
    if (!val) return;
    const mutation = isAdminTab ? postAdminComment : postComment;
    mutation({ usersGuid: user.Guid, comment: val }).then((res) => {
      if ("data" in res && res.data) setValue("");
    });
  };

  const handleSaveEdit = () => {
    if (editingCommentId == null) return;
    const trimmed = editDraft.trim();
    if (!trimmed) return;
    const mutation = isAdminTab ? patchAdminComment : patchComment;
    mutation({ id: editingCommentId, body: { comment: trimmed } }).then((res) => {
      if ("data" in res && res.data) {
        setEditingCommentId(null);
        setEditDraft("");
      }
    });
  };

  const handleTabChange = (tab: 'comments' | 'adminComments') => {
    setActiveTab(tab);
    // Reset edit state when switching tabs
    setEditingCommentId(null);
    setEditDraft("");
    setValue("");
  };

  return (
    <section className={styles.section} data-testid="comments-page" dir={i18n.dir()}>
      <BackArrowButton text={t('PREVIOUS_SCREEN')} onClick={onClose} position={"left"} />
      <h3 className={styles.title}>{t("COMMENTS_TITLE")}</h3>

      {/* Patient Info */}
      <div className={styles.patientInfo}>
        <h2>👤 {t("PATIENT_DETAILS")}</h2>
        <div className={styles.patientDetails}>
          <div><strong>{t("PATIENT_NAME")}:</strong> {user?.FirstName} {user?.LastName}</div>
          <div><strong>{t("PATIENT_BIRTH_DATE")}:</strong> {user?.Birth ? getPrintDate(new Date(user?.Birth ?? "")) : ""}</div>
          <div><strong>{t("PATIENT_PHONE")}:</strong> {user?.Phone || ""}</div>
          <div><strong>{t("EMAIL")}:</strong> {user?.Email || ""}</div>
        </div>
      </div>

      {/* Comments List */}
      <article className={styles.patientInfo}>
        {/* Tabs */}

        <h2>📝 {isAdminTab ? t("COMMENTS_ADMIN_TITLE") : t("COMMENTS_TITLE")}</h2>
        <div
          className={`${formStyles.contactTabsContainer} ${styles.contactTabsContainer}`}
        >
          <div
            className={`${formStyles.contactTabsHeader} ${styles.contactTabsHeader}`}
          >            <div className={formStyles.contactTabsList}>
              <button
                type="button"
                className={`${formStyles.contactTab} ${activeTab === 'comments' ? formStyles.contactTab_active : ''}`}
                style={{ borderRadius: '0', background: 'transparent', boxShadow: 'none' }}
                onClick={() => handleTabChange('comments')}
              >
                {t("COMMENTS_TITLE")}
              </button>
              <button
                type="button"
                className={`${formStyles.contactTab} ${activeTab === 'adminComments' ? formStyles.contactTab_active : ''}`}
                style={{ borderRadius: '0', background: 'transparent', boxShadow: 'none' }}
                onClick={() => handleTabChange('adminComments')}
              >
                {t("COMMENTS_ADMIN_TITLE")}
              </button>
            </div>
          </div>
        </div>
        <p className={styles.documentLabel}>{isAdminTab ? t("COMMENTS_ADMIN_NOTE") : t("COMMENTS_STAFF_NOTE")}</p>
        {activeIsLoading && <div>{t("LOADING")}</div>}
        {activeIsError && <div>{t("ERROR_LOADING")}</div>}
        <ul className={styles.commentsList}>
          {!activeIsLoading && activeComments && activeComments.length > 0
            ? activeComments.map((comment) => (
              <li key={comment.id} className={styles.comment}>
                <div className={styles.commentHeader}>
                  <div className={styles.patientDetails}>
                    <p className={styles.documentLabel}>
                      {t("OPENING_DATE_1")}
                      <span className={styles.documentText}>
                        {comment.createdOn
                          ? `${getPrintDate(new Date(comment.createdOn))} ${getTimeFromTimestamp(new Date(comment.createdOn))}`
                          : noDataLabel}
                      </span>
                    </p>
                    <p className={styles.documentLabel}>
                      {t("DOCUMENT_LABEL_UPDATED_BY")} <span className={styles.documentText}>{comment.creatorName}</span>
                    </p>
                  </div>
                  {admin?.id != null && comment.creatorId === admin.id && (
                    <button
                      type="button"
                      className={styles.editBtn}
                      title={t("THERAPISTS_REPORTS_EDIT_BUTTON")}
                      disabled={activeIsPatching}
                      onClick={() => {
                        setEditingCommentId(comment.id);
                        setEditDraft(comment.comment);
                      }}
                    >
                      <img src={editIcon} alt="edit comment" />
                    </button>
                  )}
                </div>

                {editingCommentId === comment.id ? (
                  <div className={styles.editBlock}>
                    <textarea
                      ref={editTextAreaRef}
                      className={`${styles.message} ${styles.textArea} ${styles.editTextarea}`}
                      rows={3}
                      value={editDraft}
                      maxLength={1200}
                      onChange={(e) => setEditDraft(e.target.value)}
                    />
                    <div className={styles.editActions}>
                      <button
                        type="button"
                        className={styles.editActionBtn}
                        disabled={activeIsPatching || !editDraft.trim()}
                        onClick={handleSaveEdit}
                      >
                        {t("SAVE")}
                      </button>
                      <button
                        type="button"
                        className={styles.editActionBtnSecondary}
                        disabled={activeIsPatching}
                        onClick={() => { setEditingCommentId(null); setEditDraft(""); }}
                      >
                        {t("CANCEL")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.commentText}>
                    {comment.comment.split("\n").map((part, index) => (
                      <span key={index} className={styles.textPart}>{part}</span>
                    ))}
                  </div>
                )}
              </li>
            ))
            : !activeIsLoading && <div className={styles.noComments}>{t("DOCUMENT_NO_UPDATES")}</div>
          }
        </ul>
      </article>

      {/* Reply Bar */}
      <div className={`${styles.patientInfo} ${styles.replyBar}`}>
        <textarea
          data-testid="message-textarea"
          className={`${styles.message} ${styles.textArea}`}
          ref={textAreaRef}
          rows={3}
          onChange={(e) => {
            const val = e.target.value;
            setValue(val.length <= 2000 ? val : val.substring(0, 2000));
          }}
          value={value}
          maxLength={2000}
        />
        <button
          data-testid="comments-send-button"
          className={`${styles.btn} ${styles.sendBtn}`}
          disabled={!value.trim() || activeIsPosting}
          onClick={() => {
            const trimmed = value.trim();
            if (!trimmed) return;
            handleSendMessage(trimmed);
          }}
        />
      </div>
    </section>
  );
}

export default UserComments;